import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { removeStudent, upsert } from '../../../Redux/studentsSlice';
import { setLessonStatus } from '../../../Redux/lessonsSlice';
import { LessonStatus, StudentKind } from '../../../Models/enums';
import { useTranslation } from '../../../i18n/useTranslation';
import type { LessonModel } from '../../../Models/LessonModel';
import type { StudentModel } from '../../../Models/StudentModel';
import { isCollected, isOwed, isScheduledLesson } from '../../../Utils/LessonStatusUtil';
import { amountValue } from '../../../Utils/MoneyUtil';
import { searchableText, studentDisplayName } from '../../../Utils/DisplayNameUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { ConfirmDialog } from '../../SharedArea/ConfirmDialog/ConfirmDialog';
import { useToast } from '../../SharedArea/Toast/ToastProvider';
import { StudentEditor } from '../StudentEditor/StudentEditor';
import { StudentCard } from './StudentCard/StudentCard';
import { StudentService } from '../../../Services/StudentService';
import { QuickAddLesson } from '../Lessons/QuickAddLesson';
import { MultiDateAdd } from '../Lessons/MultiDateAdd';
import './Students.css';

type Sort = 'name' | 'lessons' | 'income' | 'debt';
type DebtFilter = 'all' | 'with' | 'without';
type StatusFilter = 'active' | 'archived' | 'both';
/**
 * Who this screen is currently about. An organization is a card in the same
 * table, because that is what gives it debt, reminders and receipts, but it is
 * not taught and does not belong in the teaching roster or its count. Two
 * views over one list rather than two screens.
 */
type KindFilter = 'taught' | 'organizations';

interface Totals {
  lessons: number;
  collected: number;
  owed: number;
  scheduled: number;
  markedPaid: number;
}

/**
 * The roster. Each card carries what a tutor looks a student up for: their
 * price, their weekly hour and what they currently owe.
 *
 * Archived students are hidden by default rather than deleted, because
 * deleting one takes their lesson history with them, and that history is the
 * tutor's own income record.
 */
export function Students() {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const students = useAppSelector(state => state.students.items);
  const state = useAppSelector(store => store.students.state);
  const error = useAppSelector(store => store.students.error);
  const lessons = useAppSelector(store => store.lessons.items);

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('name');
  const [debtFilter, setDebtFilter] = useState<DebtFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [kindFilter, setKindFilter] = useState<KindFilter>('taught');
  // A name, not a snapshot: StudentEditor's slot and contact sub-editors save
  // straight to the store on every add or remove, and used to be handed a
  // student object captured once when the modal opened. That left them
  // rendering a stale list forever — a removed slot or contact stayed on
  // screen because the prop never saw the update, even though the delete had
  // already succeeded. Looking the student up by name every render means the
  // modal always reflects whatever is actually in the store.
  const [editingName, setEditingName] = useState<string | null | undefined>(undefined);
  const editing = editingName === undefined
    ? undefined
    : editingName === null
      ? null
      : students.find(student => student.name === editingName) ?? null;
  const [confirming, setConfirming] = useState<StudentModel | null>(null);
  const [quickAdding, setQuickAdding] = useState<StudentModel | null>(null);
  const [multiAdding, setMultiAdding] = useState<StudentModel | null>(null);
  const [collecting, setCollecting] = useState<{ student: StudentModel; owed: readonly LessonModel[] } | null>(null);

  async function setGcalRecurring(student: StudentModel, enabled: boolean) {
    const updated = await StudentService.setGcalRecurring(student.name, enabled);
    dispatch(upsert({ ...student, gcalRecurring: updated.gcalRecurring }));
  }

  function markAllPaid(owed: readonly LessonModel[]) {
    // Each lesson is its own request, because the API has no bulk endpoint and
    // inventing one for a button that touches a handful of rows is not worth
    // the migration. A failure part way through leaves the rest marked.
    for (const lesson of owed) {
      void dispatch(setLessonStatus({ id: lesson.id, status: LessonStatus.Paid }));
    }
    toast({
      message: tr('markedPaidCount', owed.length),
      // Each lesson goes back to what it was, not to a blanket unpaid: a
      // cancelled-and-charged lesson was never unpaid to begin with.
      onUndo: () => {
        for (const lesson of owed) {
          void dispatch(setLessonStatus({ id: lesson.id, status: lesson.status }));
        }
      }
    });
  }

  // Every student's lessons and money, once. Both the cards and the summary
  // bar read from this, so the two can never disagree about who owes what.
  const byStudent = useMemo(() => {
    const map = new Map<string, { lessons: LessonModel[]; totals: Totals }>();
    for (const student of students) {
      map.set(student.name, { lessons: [], totals: emptyTotals() });
    }
    for (const lesson of lessons) {
      const entry = map.get(lesson.student);
      if (!entry) continue;
      entry.lessons.push(lesson);
      const slots = students.find(student => student.name === lesson.student)?.slots ?? [];
      const amount = amountValue(lesson.amount);
      entry.totals.lessons += 1;
      if (isCollected(lesson)) {
        entry.totals.collected += amount;
        entry.totals.markedPaid += 1;
      } else if (isOwed(lesson, slots)) entry.totals.owed += amount;
      else if (isScheduledLesson(lesson, slots)) entry.totals.scheduled += amount;
    }
    return map;
  }, [students, lessons]);

  const visible = useMemo(() => {
    const locale = language === 'he' ? 'he-IL' : 'en-US';
    const needle = query.trim().toLocaleLowerCase(locale);
    const filtered = students.filter(student => {
      const isOrganization = student.kind === StudentKind.Organization;
      if ((kindFilter === 'organizations') !== isOrganization) return false;
      const archived = student.archived === 1;
      if (statusFilter === 'active' && archived) return false;
      if (statusFilter === 'archived' && !archived) return false;
      const owed = byStudent.get(student.name)?.totals.owed ?? 0;
      if (debtFilter === 'with' && owed <= 0) return false;
      if (debtFilter === 'without' && owed > 0) return false;
      if (!needle) return true;
      // Both spellings, whatever the interface language: a tutor who typed an
      // English name once should be able to find it in Hebrew and the other
      // way round, without switching the interface first.
      return searchableText(student).includes(needle);
    });

    // The name is the tiebreaker for every sort, so the order is stable rather
    // than dependent on whatever the API happened to return. Sorted by what is
    // actually on screen, or an English roster would come back in Hebrew
    // alphabetical order.
    const byName = (a: StudentModel, b: StudentModel) =>
      studentDisplayName(a, language).localeCompare(studentDisplayName(b, language), locale);
    const measure = (student: StudentModel) => {
      const totals = byStudent.get(student.name)?.totals ?? emptyTotals();
      return sort === 'lessons' ? totals.lessons : sort === 'income' ? totals.collected : totals.owed;
    };
    return filtered.sort((a, b) => (sort === 'name' ? byName(a, b) : measure(b) - measure(a) || byName(a, b)));
  }, [students, byStudent, query, sort, debtFilter, statusFilter, kindFilter, language]);

  const summary = useMemo(() => {
    // The summary describes what is on screen, so a filtered roster gets a
    // filtered total. A figure that ignored the filter would contradict the
    // cards under it.
    const totals = emptyTotals();
    let owing = 0;
    for (const student of visible) {
      const entry = byStudent.get(student.name)?.totals ?? emptyTotals();
      totals.lessons += entry.lessons;
      totals.collected += entry.collected;
      totals.owed += entry.owed;
      totals.scheduled += entry.scheduled;
      totals.markedPaid += entry.markedPaid;
      if (entry.owed > 0) owing += 1;
    }
    return { totals, owing };
  }, [visible, byStudent]);

  const filtering = Boolean(query.trim()) || debtFilter !== 'all' || statusFilter !== 'active';

  if (state === 'loading' || state === 'idle') return <Spinner />;
  if (state === 'failed') return <ErrorState code={error} />;

  return (
    <section className="students">
      <header className="students-header">
        <h1 className="screen-title">{tr('navStudents')}</h1>
        <button type="button" className="primary-button students-add-btn" onClick={() => setEditingName(null)} aria-label={tr('addStudent')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" /></svg>
          <span>{tr('addStudent')}</span>
        </button>
      </header>

      <div className="students-filters">
        <input
          type="search"
          className="text-input"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={tr('search')}
          aria-label={tr('search')}
        />
        <select className="text-input" value={sort} onChange={event => setSort(event.target.value as Sort)} aria-label={tr('sortLabel')}>
          <option value="name">{tr('sortByName')}</option>
          <option value="lessons">{tr('sortByLessons')}</option>
          <option value="income">{tr('sortByIncome')}</option>
          <option value="debt">{tr('sortByDebt')}</option>
        </select>
        <select className="text-input" value={debtFilter} onChange={event => setDebtFilter(event.target.value as DebtFilter)} aria-label={tr('debtFilterLabel')}>
          <option value="all">{tr('debtFilterAll')}</option>
          <option value="with">{tr('debtFilterWith')}</option>
          <option value="without">{tr('debtFilterWithout')}</option>
        </select>
        <select className="text-input" value={statusFilter} onChange={event => setStatusFilter(event.target.value as StatusFilter)} aria-label={tr('statusFilterLabel')}>
          <option value="active">{tr('statusFilterActive')}</option>
          <option value="archived">{tr('statusFilterArchived')}</option>
          <option value="both">{tr('statusFilterBoth')}</option>
        </select>
        <select className="text-input" value={kindFilter} onChange={event => setKindFilter(event.target.value as KindFilter)} aria-label={tr('kindFilterLabel')}>
          <option value="taught">{tr('kindFilterTaught')}</option>
          <option value="organizations">{tr('kindFilterOrganizations')}</option>
        </select>
      </div>

      <div className="students-summary" aria-label={tr('dashboardSummary')}>
        <span>
          <strong>{visible.length.toLocaleString()}</strong>
          {kindFilter === 'organizations' ? tr('kindFilterOrganizations') : tr('rosterStudents')}
        </span>
        <span className={summary.totals.owed > 0 ? 'is-owed' : ''}>
          <strong><MoneyText amount={summary.totals.owed} /></strong>{tr('debt')}
        </span>
        <span><strong>{summary.owing.toLocaleString()}</strong>{tr('rosterOwingCount')}</span>
        <span><strong><MoneyText amount={summary.totals.scheduled} /></strong>{tr('scheduledIncome')}</span>
        <span><strong>{summary.totals.markedPaid.toLocaleString()}</strong>{tr('rosterMarkedPaid')}</span>
      </div>

      {visible.length === 0
        ? filtering
          ? <EmptyState title={tr('noStudentsMatch')} hint={tr('noStudentsMatchHint')} />
          : kindFilter === 'organizations'
            ? <EmptyState title={tr('noOrganizationsYet')} hint={tr('noOrganizationsHint')} />
            : <EmptyState title={tr('noStudents')} hint={tr('noStudentsHint')} />
        : (
          <ul className="student-cards">
            {visible.map(student => (
              <StudentCard
                key={student.name}
                student={student}
                lessons={byStudent.get(student.name)?.lessons ?? []}
                onEdit={student => setEditingName(student.name)}
                onDelete={setConfirming}
                onQuickAdd={setQuickAdding}
                onMultiAdd={setMultiAdding}
                onMarkAllPaid={(target, owed) => setCollecting({ student: target, owed })}
                onGcalChange={setGcalRecurring}
              />
            ))}
          </ul>
        )}

      {editing !== undefined && (
        <StudentEditor student={editing} onClose={() => setEditingName(undefined)} />
      )}

      {quickAdding && <QuickAddLesson initialStudentName={quickAdding.name} onClose={() => setQuickAdding(null)} />}

      {multiAdding && <MultiDateAdd student={multiAdding} onClose={() => setMultiAdding(null)} />}

      {collecting && (
        <ConfirmDialog
          title={tr('markAllPaidTitle', studentDisplayName(collecting.student, language))}
          body={tr('markAllPaidBody')}
          confirmLabel={tr('markAllPaid')}
          onCancel={() => setCollecting(null)}
          onConfirm={() => {
            markAllPaid(collecting.owed);
            setCollecting(null);
          }}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title={tr('deleteStudentTitle', studentDisplayName(confirming, language))}
          // The cost is stated plainly, because it is not recoverable.
          body={tr('deleteStudentBody')}
          confirmLabel={tr('delete')}
          destructive
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            void dispatch(removeStudent(confirming.name));
            setConfirming(null);
          }}
        />
      )}
    </section>
  );
}

function emptyTotals(): Totals {
  return { lessons: 0, collected: 0, owed: 0, scheduled: 0, markedPaid: 0 };
}
